import { ensureChatHubStarted, getChatHubIfConnected, releaseChatHub } from "@/domains/offline/chat/chatHubManager";
import { Roles } from "@/domains/shared/models/roles";
import { HubConnectionState } from "@microsoft/signalr";
import { useEffect, useMemo, useState } from "react";

type ConversationUnreadRow = {
  unreadCount?: number;
};

const UNREAD_POLL_MS = 10000;

export function useUnreadMessages(role?: string, emailVerified?: boolean, enabled: boolean = true) {
  const [totalUnread, setTotalUnread] = useState(0);

  const canUseUnread = useMemo(
    () => enabled && (role === Roles.Parent || role === Roles.Therapist) && emailVerified === true,
    [enabled, role, emailVerified],
  );

  useEffect(() => {
    if (!canUseUnread) {
      setTotalUnread(0);
      return;
    }

    let cancelled = false;
    let holdReleased = false;
    let connection: Awaited<ReturnType<typeof ensureChatHubStarted>> | null = null;

    const pullUnread = async () => {
      const conn = connection ?? getChatHubIfConnected();
      if (cancelled || !conn || conn.state !== HubConnectionState.Connected) {
return;
}
      try {
        const rows = await conn.invoke<ConversationUnreadRow[]>("GetConversations", "family");
        if (cancelled) {
return;
}
        const nextTotal = rows.reduce((sum, item) => sum + Math.max(0, item.unreadCount ?? 0), 0);
        setTotalUnread(nextTotal);
      } catch {
        // Keep stale value; polling will retry.
      }
    };

    void ensureChatHubStarted()
      .then((conn) => {
        if (cancelled) {
          if (!holdReleased) {
            releaseChatHub();
            holdReleased = true;
          }
          return;
        }
        connection = conn;
        conn.onreconnected(() => {
          if (!cancelled) {
void pullUnread();
}
        });
        void pullUnread();
      })
      .catch(() => {
        if (!holdReleased) {
          releaseChatHub();
          holdReleased = true;
        }
      });

    const interval = window.setInterval(() => {
      if (document.visibilityState === "hidden") {
return;
}
      void pullUnread();
    }, UNREAD_POLL_MS);

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
void pullUnread();
}
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      if (!holdReleased) {
        releaseChatHub();
        holdReleased = true;
      }
    };
  }, [canUseUnread]);

  return { totalUnread };
}
